import React from 'react';
import { BookOpen, Target, Users, Zap, TrendingUp, QrCode, Mail, BarChart, Gamepad2, Crown } from 'lucide-react';

const BootCampGuide = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-8 mb-8 shadow-xl">
          <BookOpen className="w-16 h-16 mb-4" />
          <h1 className="text-4xl font-bold mb-2">BBC Quick List Boot Camp Guide</h1>
          <p className="text-xl text-blue-100">Your Complete Guide to Building a Digital Empire</p>
        </div>

        {/* Table of Contents */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold mb-4">📋 Table of Contents</h2>
          <ul className="space-y-2">
            <li><a href="#welcome" className="text-blue-600 hover:underline">1. Welcome to BBC Quick List</a></li>
            <li><a href="#getting-started" className="text-blue-600 hover:underline">2. Getting Started</a></li>
            <li><a href="#lead-capture" className="text-blue-600 hover:underline">3. Lead Capture System</a></li>
            <li><a href="#qr-codes" className="text-blue-600 hover:underline">4. QR Code Generator</a></li>
            <li><a href="#marketing-library" className="text-blue-600 hover:underline">5. Marketing Library</a></li>
            <li><a href="#pif-system" className="text-blue-600 hover:underline">6. PIF Voucher System</a></li>
            <li><a href="#matrix" className="text-blue-600 hover:underline">7. Matrix Compensation Engine</a></li>
            <li><a href="#game-system" className="text-blue-600 hover:underline">8. Game Dashboard</a></li>
            <li><a href="#training-vault" className="text-blue-600 hover:underline">9. Boot Camp Training Vault</a></li>
            <li><a href="#best-practices" className="text-blue-600 hover:underline">10. Best Practices & Tips</a></li>
          </ul>
        </div>

        {/* Section 1: Welcome */}
        <section id="welcome" className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <Target className="w-8 h-8 text-blue-600" />
            1. Welcome to BBC Quick List
          </h2>
          <div className="prose max-w-none">
            <p className="text-lg mb-4">
              BBC Quick List is more than just a lead capture platform - it's a complete business-building ecosystem designed to help you create, capture, and convert leads while building network leverage through our compensation engine.
            </p>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">What Makes Us Different?</h3>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Non-Custodial Platform:</strong> We never hold your money. All payments flow directly member-to-member using cryptocurrency.</li>
              <li><strong>Educational Focus:</strong> Every tool teaches real marketing skills that translate to real income.</li>
              <li><strong>Game-Based Learning:</strong> Turn list building into an engaging game with missions, power-ups, and leaderboards.</li>
              <li><strong>Forced Matrix System:</strong> Build network leverage with our 1x2 and 12x3 compensation matrices.</li>
              <li><strong>Complete Marketing Suite:</strong> Landing pages, QR codes, email sequences, and more - all in one place.</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">Your Journey</h3>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="mb-2"><strong>Rookie Boot Camp</strong> → Learn fundamentals, capture leads, earn first commissions</p>
              <p className="mb-2"><strong>Phase 1 (1x2 Matrix)</strong> → Build your foundation with 2 direct referrals</p>
              <p className="mb-2"><strong>Phase 2 (12x3 Matrix)</strong> → Scale with 12+ referrals and exponential growth</p>
              <p><strong>Enterprise Level</strong> → Unlock advanced tools and maximum earning potential</p>
            </div>
          </div>
        </section>

        {/* Section 2: Getting Started */}
        <section id="getting-started" className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <Zap className="w-8 h-8 text-yellow-600" />
            2. Getting Started
          </h2>
          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold mb-3">Step 1: Complete Your Profile</h3>
            <p className="mb-4">
              Navigate to your Member Dashboard and complete the onboarding wizard. This sets up your business headquarters:
            </p>
            <ul className="list-disc ml-6 space-y-2 mb-6">
              <li><strong>Tron Wallet:</strong> Add your TRX wallet address (where commissions will flow)</li>
              <li><strong>Avatar:</strong> Upload a professional photo or choose an avatar</li>
              <li><strong>Mini Bio:</strong> Write a compelling 2-3 sentence bio about your business</li>
              <li><strong>Tour:</strong> Complete the platform tour to earn bonus XP and credits</li>
            </ul>

            <div className="bg-green-50 p-4 rounded-lg mb-6">
              <p className="font-semibold text-green-800 mb-2">🎁 Profile Completion Rewards:</p>
              <ul className="list-disc ml-6">
                <li>Wallet Setup: +25 credits</li>
                <li>Avatar Upload: +10 credits</li>
                <li>Bio Written: +15 credits</li>
                <li>Full Completion: +50 XP bonus</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold mb-3">Step 2: Understand Your Dashboard</h3>
            <p className="mb-4">Your Member Dashboard is your command center with quick access to:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Lead Capture:</strong> Create and manage landing pages</li>
              <li><strong>My Leads:</strong> View captured leads and conversion stats</li>
              <li><strong>Marketing Library:</strong> Access pre-made content and campaigns</li>
              <li><strong>Game Dashboard:</strong> Track points, missions, and leaderboard position</li>
              <li><strong>Matrix Overview:</strong> Monitor your network and earnings</li>
              <li><strong>Training Vault:</strong> Access courses and certification tracks</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">Step 3: Purchase Your First PIF Voucher</h3>
            <p className="mb-4">
              To start building your network, purchase a $95 PIF voucher. This allows you to sponsor someone into the platform. The voucher system has three assignment windows:
            </p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Sponsor Window (24h):</strong> Assign to your direct referrals first</li>
              <li><strong>Team Window (48h):</strong> Assign to your team's referrals</li>
              <li><strong>Global Window (72h):</strong> Assign to anyone in the holding tank (limits apply)</li>
            </ul>
          </div>
        </section>

        {/* Section 3: Lead Capture */}
        <section id="lead-capture" className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <Target className="w-8 h-8 text-green-600" />
            3. Lead Capture System
          </h2>
          <div className="prose max-w-none">
            <p className="text-lg mb-4">
              The Lead Capture System is your primary tool for building your list. Every lead you capture earns you game points and brings you closer to mission rewards.
            </p>

            <h3 className="text-xl font-semibold mb-3">Creating a Landing Page</h3>
            <ol className="list-decimal ml-6 space-y-3 mb-6">
              <li><strong>Navigate:</strong> Go to Lead Capture → Template Marketplace</li>
              <li><strong>Choose Template:</strong> Browse pre-built templates or create custom</li>
              <li><strong>Customize:</strong> Add your headline, description, call-to-action</li>
              <li><strong>Configure:</strong> Set up what information you want to collect (name, email, phone)</li>
              <li><strong>Publish:</strong> Get your unique URL and start sharing</li>
            </ol>

            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <p className="font-semibold text-blue-800 mb-2">💡 Pro Tip:</p>
              <p>Qualified leads (email + phone) earn 35 points instead of 10. Always ask for both!</p>
            </div>

            <h3 className="text-xl font-semibold mb-3">Template Types Available</h3>
            <ul className="list-disc ml-6 space-y-2 mb-6">
              <li><strong>Insurance (PHP):</strong> Pre-approved for insurance professionals</li>
              <li><strong>Legal Services (LegalShield):</strong> Attorney referral network pages</li>
              <li><strong>General Money-Making:</strong> Opportunity seeker templates</li>
              <li><strong>eBook Opt-In:</strong> Lead magnet delivery pages</li>
              <li><strong>Webinar Registration:</strong> Event signup forms</li>
              <li><strong>Custom:</strong> Build from scratch with drag-and-drop</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">Lead Management</h3>
            <p className="mb-4">View all captured leads in My Leads dashboard:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Filter by Source:</strong> See which pages convert best</li>
              <li><strong>Export to CSV:</strong> Download for email marketing</li>
              <li><strong>Assign Tags:</strong> Organize leads by interest/status</li>
              <li><strong>Follow-Up Sequences:</strong> Set up automated email nurture campaigns</li>
            </ul>

            <div className="bg-yellow-50 p-4 rounded-lg mt-6">
              <p className="font-semibold text-yellow-800 mb-2">⚠️ Important:</p>
              <p>Every lead captured automatically awards game points and progresses your missions. The fortune is in the follow-up - set up email sequences within 24 hours for best conversion rates!</p>
            </div>
          </div>
        </section>

        {/* Section 4: QR Codes */}
        <section id="qr-codes" className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <QrCode className="w-8 h-8 text-purple-600" />
            4. QR Code Generator
          </h2>
          <div className="prose max-w-none">
            <p className="text-lg mb-4">
              QR codes bridge offline and online marketing. Place them on business cards, flyers, posters, or social media to capture leads instantly.
            </p>

            <h3 className="text-xl font-semibold mb-3">Creating a QR Code</h3>
            <ol className="list-decimal ml-6 space-y-3 mb-6">
              <li><strong>Navigate:</strong> Tools → QR Code Generator</li>
              <li><strong>Select Page:</strong> Choose which landing page to link</li>
              <li><strong>Customize:</strong> Add logo, change colors, set size</li>
              <li><strong>Download:</strong> Get PNG or SVG for printing</li>
              <li><strong>Track:</strong> Monitor scans in analytics dashboard</li>
            </ol>

            <h3 className="text-xl font-semibold mb-3">Where to Use QR Codes</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Physical Locations:</h4>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Business cards</li>
                  <li>Flyers and posters</li>
                  <li>Trade show booth</li>
                  <li>Vehicle wraps</li>
                  <li>Product packaging</li>
                </ul>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Digital Channels:</h4>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Social media posts</li>
                  <li>Email signatures</li>
                  <li>YouTube video descriptions</li>
                  <li>LinkedIn profile</li>
                  <li>Instagram bio</li>
                </ul>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <p className="font-semibold text-green-800 mb-2">🎯 Daily Mission Alert:</p>
              <p>"QR Quest" mission: Generate 1 QR code and track scans = +30 XP daily!</p>
            </div>
          </div>
        </section>

        {/* Section 5: Marketing Library */}
        <section id="marketing-library" className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <Mail className="w-8 h-8 text-red-600" />
            5. Marketing Library
          </h2>
          <div className="prose max-w-none">
            <p className="text-lg mb-4">
              Access professionally written marketing content including email sequences, social media posts, ad copy, and more. No need to write from scratch - customize and deploy.
            </p>

            <h3 className="text-xl font-semibold mb-3">Available Content Types</h3>
            <ul className="list-disc ml-6 space-y-3 mb-6">
              <li>
                <strong>Email Sequences:</strong> 7-day, 14-day, and 30-day nurture campaigns
                <ul className="list-circle ml-6 mt-2">
                  <li>Welcome series for new leads</li>
                  <li>Educational nurture sequences</li>
                  <li>Sales conversion campaigns</li>
                  <li>Re-engagement sequences</li>
                </ul>
              </li>
              <li>
                <strong>Social Media Templates:</strong> Ready-to-post content for Facebook, Instagram, LinkedIn
              </li>
              <li>
                <strong>Ad Copy:</strong> Proven headlines and body copy for paid advertising
              </li>
              <li>
                <strong>Scripts:</strong> Phone, video, and presentation scripts
              </li>
              <li>
                <strong>eBooks:</strong> Three comprehensive lead magnets (download and brand as your own)
              </li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">How to Use Content</h3>
            <ol className="list-decimal ml-6 space-y-2">
              <li>Browse the library by category or search</li>
              <li>Preview content to see if it fits your audience</li>
              <li>Copy to your workspace</li>
              <li>Customize with your personal touch</li>
              <li>Deploy via email, social, or landing pages</li>
            </ol>

            <div className="bg-blue-50 p-4 rounded-lg mt-6">
              <p className="font-semibold text-blue-800 mb-2">💡 Content Strategy Tip:</p>
              <p>Use the "Morning Traffic" template for peak conversion times (7-10 AM). Pair with QR codes for maximum reach!</p>
            </div>
          </div>
        </section>

        {/* Section 6: PIF System */}
        <section id="pif-system" className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <Users className="w-8 h-8 text-indigo-600" />
            6. PIF Voucher System (Pay It Forward)
          </h2>
          <div className="prose max-w-none">
            <p className="text-lg mb-4">
              The PIF system is how you build network leverage. Each $95 voucher sponsors one person into the platform, activating their matrix position and progressing your own Phase 2 unlock.
            </p>

            <h3 className="text-xl font-semibold mb-3">How PIF Works</h3>
            <div className="space-y-4 mb-6">
              <div className="bg-indigo-50 p-4 rounded-lg">
                <p className="font-semibold mb-2">1. Purchase Voucher</p>
                <p>Buy a $95 PIF voucher using USDT via NOWPayments. Payment goes directly member-to-member (non-custodial).</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg">
                <p className="font-semibold mb-2">2. Assignment Windows</p>
                <ul className="list-disc ml-6 mt-2">
                  <li><strong>0-24h (Sponsor):</strong> Assign to your direct referrals</li>
                  <li><strong>24-48h (Team):</strong> Assign to your team's referrals</li>
                  <li><strong>48-72h (Global):</strong> Assign to anyone (1/day, 3/week limit)</li>
                </ul>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg">
                <p className="font-semibold mb-2">3. Claim & Activation</p>
                <p>When prospect claims the voucher code, their matrix position activates. Your PIF count increments toward Phase 2 unlock (12 needed).</p>
              </div>
            </div>

            <h3 className="text-xl font-semibold mb-3">Game Rewards for PIF</h3>
            <ul className="list-disc ml-6 space-y-2 mb-6">
              <li><strong>First PIF:</strong> +100 points + "PIF Master" badge + voiceover celebration</li>
              <li><strong>5 PIFs:</strong> Halfway milestone reward + motivational tip</li>
              <li><strong>12 PIFs:</strong> Phase 2 unlock + 150 credit bonus + "Phase 2 Champion" badge</li>
            </ul>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="font-semibold text-yellow-800 mb-2">⚡ Power Move:</p>
              <p>Purchase multiple vouchers in advance. When your leads convert, you can assign immediately without waiting for payment processing!</p>
            </div>
          </div>
        </section>

        {/* Section 7: Matrix */}
        <section id="matrix" className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-green-600" />
            7. Matrix Compensation Engine
          </h2>
          <div className="prose max-w-none">
            <p className="text-lg mb-4">
              BBC Quick List uses a forced matrix system that creates predictable, scalable income. Your position fills automatically as new members join above you.
            </p>

            <h3 className="text-xl font-semibold mb-3">Phase 1: 1x2 Matrix</h3>
            <div className="bg-green-50 p-4 rounded-lg mb-6">
              <ul className="space-y-2">
                <li><strong>Structure:</strong> You sponsor 2 people directly</li>
                <li><strong>Entry:</strong> $95 PIF voucher</li>
                <li><strong>Earnings:</strong> Direct commissions on your 2 referrals</li>
                <li><strong>Auto-Repurchase:</strong> System can auto-buy next voucher from earnings</li>
                <li><strong>Goal:</strong> Build foundation and reach 12 total PIFs</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold mb-3">Phase 2: 12x3 Matrix (Unlocks at 12 PIFs)</h3>
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <ul className="space-y-2">
                <li><strong>Structure:</strong> 12 wide x 3 deep = exponential growth</li>
                <li><strong>Level 1:</strong> 12 positions (your direct referrals)</li>
                <li><strong>Level 2:</strong> 144 positions (their referrals)</li>
                <li><strong>Level 3:</strong> 1,728 positions (spillover)</li>
                <li><strong>Total Network:</strong> 1,884 positions possible</li>
                <li><strong>Earnings:</strong> Commissions on all three levels</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold mb-3">How Spillover Works</h3>
            <p className="mb-4">
              When someone above you sponsors more than 2 people (Phase 1) or 12 people (Phase 2), the extra positions "spill over" into your matrix. This creates passive growth - your matrix can fill even when you're not actively recruiting.
            </p>

            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="font-semibold text-purple-800 mb-2">🚀 Exponential Example:</p>
              <p className="mb-2">If you sponsor 2 people (Phase 1):</p>
              <ul className="list-disc ml-6">
                <li>You earn on 2 positions = $X</li>
                <li>They each sponsor 2 = 4 more positions in your team</li>
                <li>After Phase 2 unlock with 12x3...</li>
                <li>Full matrix = 1,884 positions = significant monthly income</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 8: Game System */}
        <section id="game-system" className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <Gamepad2 className="w-8 h-8 text-yellow-600" />
            8. Game Dashboard
          </h2>
          <div className="prose max-w-none">
            <p className="text-lg mb-4">
              Turn list building into a game! Earn points, complete missions, purchase power-ups, and climb the leaderboard while building real business skills.
            </p>

            <h3 className="text-xl font-semibold mb-3">How to Earn Points</h3>
            <ul className="list-disc ml-6 space-y-2 mb-6">
              <li><strong>Lead Captured:</strong> 10 points (base)</li>
              <li><strong>Qualified Lead:</strong> +25 points (email + phone) = 35 total</li>
              <li><strong>PIF Conversion:</strong> 100 points</li>
              <li><strong>Daily Login:</strong> 5 points</li>
              <li><strong>3-Day Streak:</strong> +50 bonus</li>
              <li><strong>7-Day Streak:</strong> +150 bonus</li>
              <li><strong>30-Day Streak:</strong> +1,000 bonus</li>
              <li><strong>Mission Complete:</strong> 50-500 points depending on difficulty</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">Mission Types</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Daily Missions</h4>
                <ul className="list-disc ml-4 space-y-1 text-sm">
                  <li>Morning Momentum (3 leads before noon)</li>
                  <li>Social Share (2 posts)</li>
                  <li>QR Quest (1 code generated)</li>
                </ul>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Weekly Missions</h4>
                <ul className="list-disc ml-4 space-y-1 text-sm">
                  <li>List Builder Pro (50 leads)</li>
                  <li>Multi-Source Master (3 sources)</li>
                  <li>Conversion King (2 PIFs)</li>
                </ul>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Seasonal Missions</h4>
                <ul className="list-disc ml-4 space-y-1 text-sm">
                  <li>Century Club (100 leads)</li>
                  <li>PIF Champion (5 PIFs)</li>
                  <li>Page Master (10 pages published)</li>
                </ul>
              </div>
            </div>

            <h3 className="text-xl font-semibold mb-3">Power-Up Shop</h3>
            <p className="mb-4">Spend credits on temporary boosts:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>2x Lead Points (50 credits):</strong> 1 hour of double points</li>
              <li><strong>3x Mega Multiplier (200 credits):</strong> 30 minutes of triple points</li>
              <li><strong>Auto-QR Share (100 credits):</strong> 24h automated social sharing</li>
              <li><strong>Lead Magnet Boost (150 credits):</strong> Featured placement for 24h</li>
              <li><strong>Speed Boost (75 credits):</strong> Faster page loading for 2 hours</li>
            </ul>

            <div className="bg-red-50 p-4 rounded-lg mt-6">
              <p className="font-semibold text-red-800 mb-2">🏆 Leaderboard Strategy:</p>
              <p>Activate 2x Points power-up during your highest traffic times. Capture 10 qualified leads in 1 hour = 700 points (vs 350 normally)!</p>
            </div>
          </div>
        </section>

        {/* Section 9: Training Vault */}
        <section id="training-vault" className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-blue-600" />
            9. Boot Camp Training Vault
          </h2>
          <div className="prose max-w-none">
            <p className="text-lg mb-4">
              Access comprehensive training courses that teach marketing fundamentals, advanced strategies, and platform mastery. Each course includes quizzes and certification.
            </p>

            <h3 className="text-xl font-semibold mb-3">Available Courses</h3>
            <div className="space-y-4">
              <div className="border-l-4 border-blue-600 pl-4">
                <h4 className="font-semibold mb-1">1. List Building Fundamentals</h4>
                <p className="text-sm text-gray-600">Learn the psychology of opt-ins, headline formulas, and conversion optimization.</p>
              </div>
              <div className="border-l-4 border-green-600 pl-4">
                <h4 className="font-semibold mb-1">2. Lead Magnet Creation</h4>
                <p className="text-sm text-gray-600">How to create irresistible offers that make people give you their contact info.</p>
              </div>
              <div className="border-l-4 border-purple-600 pl-4">
                <h4 className="font-semibold mb-1">3. Email Marketing Mastery</h4>
                <p className="text-sm text-gray-600">Write emails that get opened, read, and clicked. Includes 30+ templates.</p>
              </div>
              <div className="border-l-4 border-yellow-600 pl-4">
                <h4 className="font-semibold mb-1">4. Traffic Generation</h4>
                <p className="text-sm text-gray-600">Free and paid methods to drive targeted visitors to your landing pages.</p>
              </div>
              <div className="border-l-4 border-red-600 pl-4">
                <h4 className="font-semibold mb-1">5. Matrix Building Strategies</h4>
                <p className="text-sm text-gray-600">How to build deep vs wide, spillover optimization, and team motivation.</p>
              </div>
            </div>

            <h3 className="text-xl font-semibold mt-6 mb-3">Certification Path</h3>
            <p className="mb-4">Complete courses and pass quizzes to earn certifications:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Rookie Certified:</strong> Complete 3 courses</li>
              <li><strong>Builder Certified:</strong> Complete 6 courses + 50 leads captured</li>
              <li><strong>Pro Certified:</strong> Complete 10 courses + Phase 2 unlocked</li>
              <li><strong>Master Certified:</strong> All courses + mentor 5 teammates</li>
            </ul>
          </div>
        </section>

        {/* Section 10: Best Practices */}
        <section id="best-practices" className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <Crown className="w-8 h-8 text-yellow-600" />
            10. Best Practices & Pro Tips
          </h2>
          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold mb-3">Daily Success Routine</h3>
            <div className="bg-green-50 p-4 rounded-lg mb-6">
              <ol className="list-decimal ml-6 space-y-2">
                <li>Login daily to maintain streak (5 points minimum)</li>
                <li>Check advisor tips for personalized guidance</li>
                <li>Complete 3 daily missions (150+ points)</li>
                <li>Capture at least 3 leads before noon (Morning Momentum mission)</li>
                <li>Follow up with yesterday's leads within 24 hours</li>
                <li>Share 2 QR codes on social media (Social Share mission)</li>
                <li>Review leaderboard and adjust strategy</li>
              </ol>
            </div>

            <h3 className="text-xl font-semibold mb-3">Conversion Optimization</h3>
            <ul className="list-disc ml-6 space-y-2 mb-6">
              <li><strong>Headlines:</strong> Use curiosity + benefit (e.g., "Discover the 3-Step System That Generated 10,000 Leads")</li>
              <li><strong>CTA Buttons:</strong> Action-oriented (not "Submit" - use "Get Instant Access")</li>
              <li><strong>Social Proof:</strong> Add testimonials and member count to landing pages</li>
              <li><strong>Mobile First:</strong> 70% of traffic is mobile - test on phone before publishing</li>
              <li><strong>A/B Testing:</strong> Run split tests after 100 visits to find winners</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">Traffic Generation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Free Methods:</h4>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Facebook groups (provide value first)</li>
                  <li>LinkedIn articles and comments</li>
                  <li>Instagram stories with QR codes</li>
                  <li>YouTube video descriptions</li>
                  <li>TikTok bio link</li>
                  <li>Email signature</li>
                </ul>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Paid Methods:</h4>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Facebook ads ($5/day minimum)</li>
                  <li>Google search ads (high intent)</li>
                  <li>Instagram influencer shoutouts</li>
                  <li>Solo ads to targeted lists</li>
                  <li>Promoted posts on LinkedIn</li>
                </ul>
              </div>
            </div>

            <h3 className="text-xl font-semibold mb-3">Team Building Tips</h3>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Lead by Example:</strong> Capture leads consistently before asking your team to</li>
              <li><strong>Train Your Frontline:</strong> Help your first 2 referrals get their 2 (creates momentum)</li>
              <li><strong>Weekly Team Calls:</strong> Share wins, provide motivation, answer questions</li>
              <li><strong>Recognize Top Performers:</strong> Celebrate team members who hit milestones</li>
              <li><strong>Share Resources:</strong> Give your team access to your best-performing pages</li>
            </ul>

            <div className="bg-yellow-50 p-4 rounded-lg mt-6">
              <p className="font-semibold text-yellow-800 mb-2">⚡ The 80/20 Rule:</p>
              <p>80% of your results will come from 20% of your activities. Focus on:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>Daily lead capture (compound growth)</li>
                <li>Immediate follow-up (conversion rates drop 50% after 24h)</li>
                <li>Helping your frontline succeed (their success = your spillover)</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Conclusion */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl p-8 shadow-xl text-center">
          <h2 className="text-3xl font-bold mb-4">You're Ready to Build Your Empire!</h2>
          <p className="text-xl mb-6">
            This guide is your roadmap. Bookmark it, refer back often, and remember: consistency beats intensity every time. Small daily actions compound into massive results.
          </p>
          <div className="bg-white/20 rounded-lg p-4 inline-block">
            <p className="font-semibold mb-2">Your First 7 Days:</p>
            <ul className="text-left space-y-1">
              <li>✅ Day 1: Complete profile, capture first lead</li>
              <li>✅ Day 2-3: Capture 5 leads, complete daily missions</li>
              <li>✅ Day 4-5: Purchase first PIF voucher, create QR codes</li>
              <li>✅ Day 6-7: Sponsor first person, set up email sequence</li>
            </ul>
          </div>
          <p className="mt-6 text-blue-100">
            Welcome to the BBC Quick List family. Let's build something great together! 🚀
          </p>
        </div>

        {/* Print/Export Button */}
        <div className="text-center mt-8">
          <button
            onClick={() => window.print()}
            className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 shadow-lg"
          >
            📄 Print or Save as PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default BootCampGuide;
